import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  Bell,
  Plane,
  Tag,
  Calendar,
  Info,
  Check,
  Trash2,
  Home,
  Bookmark,
  MapPin,
  Users,
  User,
  Loader2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import FloatingSupport from '../components/FloatingSupport';

// Panchi Bird Logo
const PanchiBirdLogo = ({ className = "w-10 h-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_panchi-travel-1/artifacts/5hhgrsb0_20260119_170240_0000.png"
    alt="Panchi"
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

const NOTIFICATION_ICONS = {
  price_drop: Tag,
  travel_reminder: Plane,
  event: Calendar,
  system: Info,
};

const NOTIFICATION_COLORS = {
  price_drop: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
  travel_reminder: { bg: 'bg-teal-100', text: 'text-teal-600' },
  event: { bg: 'bg-amber-100', text: 'text-amber-600' },
  system: { bg: 'bg-gray-100', text: 'text-gray-600' },
};

export default function Notifications() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    loadNotifications();
  }, [isAuthenticated, navigate]);
  
  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await api.getNotifications(user?.user_id || 'default');
      setNotifications(data.notifications || generateMockNotifications());
    } catch (error) {
      console.error('Notifications error:', error);
      setNotifications(generateMockNotifications());
    } finally {
      setLoading(false);
    }
  };
  
  const generateMockNotifications = () => [
    {
      id: '1',
      type: 'price_drop',
      title: 'Price Drop Alert! 🎉',
      message: 'Flights to Goa dropped by 30%. Book now before prices rise!',
      created_at: new Date().toISOString(),
      read: false,
    },
    {
      id: '2',
      type: 'travel_reminder',
      title: 'Upcoming Trip',
      message: 'Your trip to Manali is in 7 days. Time to pack!',
      created_at: new Date(Date.now() - 3600000).toISOString(),
      read: false,
    },
    {
      id: '3',
      type: 'event',
      title: 'Event Near You',
      message: 'Music Festival starts this weekend in your saved destination.',
      created_at: new Date(Date.now() - 86400000).toISOString(),
      read: true,
    },
    {
      id: '4',
      type: 'system',
      title: 'Welcome to Panchi! 🐦',
      message: 'Start planning your dream trip with AI-powered recommendations.',
      created_at: new Date(Date.now() - 172800000).toISOString(),
      read: true,
    },
  ];
  
  const markAsRead = async (notificationId) => {
    setNotifications(prev => prev.map(n => 
      n.id === notificationId ? { ...n, read: true } : n
    ));
  };
  
  const deleteNotification = async (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };
  
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };
  
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };
  
  const unreadCount = notifications.filter(n => !n.read).length;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          
          <div className="flex items-center gap-2">
            <PanchiBirdLogo className="w-8 h-8" />
            <span className="font-bold text-gray-900">Notifications</span>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 bg-teal-100 text-teal-600 text-xs font-bold" style={{ borderRadius: '9999px' }}>
                {unreadCount}
              </span>
            )}
          </div>
          
          {unreadCount > 0 && (
            <button 
              onClick={markAllAsRead}
              className="text-sm text-teal-600 font-medium hover:text-teal-500"
              data-testid="mark-all-read"
            >
              Mark all read
            </button>
          )}
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-teal-500 mb-4" />
            <p className="text-gray-500">Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 bg-teal-100 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '1.5rem' }}>
              <Bell size={40} className="text-teal-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No notifications yet</h3>
            <p className="text-gray-500 mb-6">{"We'll notify you about price drops, trip reminders, and more!"}</p>
            <Link 
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 font-semibold hover:border-teal-300 transition-all"
              style={{ borderRadius: '9999px' }}
            >
              Explore Destinations
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification, index) => {
              const Icon = NOTIFICATION_ICONS[notification.type] || Bell;
              const colors = NOTIFICATION_COLORS[notification.type] || NOTIFICATION_COLORS.system;
              
              return (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => markAsRead(notification.id)}
                  className={`p-4 bg-white/80 backdrop-blur border shadow transition-all cursor-pointer ${
                    notification.read 
                      ? 'border-gray-100' 
                      : 'border-teal-200 bg-teal-50/50'
                  }`}
                  style={{ borderRadius: '1rem' }}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 ${colors.bg} flex items-center justify-center flex-shrink-0`} style={{ borderRadius: '0.75rem' }}>
                      <Icon size={22} className={colors.text} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <h4 className={`font-semibold ${notification.read ? 'text-gray-700' : 'text-gray-900'}`}>
                          {notification.title}
                        </h4>
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {formatTime(notification.created_at)}
                        </span>
                      </div>
                      <p className={`text-sm ${notification.read ? 'text-gray-500' : 'text-gray-600'}`}>
                        {notification.message}
                      </p>
                    </div>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification.id);
                      }}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                      style={{ borderRadius: '50%' }}
                      data-testid={`delete-${notification.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
      
      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 bg-white border-t border-gray-100">
        <div className="flex items-center justify-around py-2">
          <Link to="/" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Home size={22} />
            <span className="text-xs font-medium">Home</span>
          </Link>
          <Link to="/saved" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Bookmark size={22} />
            <span className="text-xs font-medium">Saved</span>
          </Link>
          <Link 
            to="/planning"
            className="w-14 h-14 -mt-6 bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30 flex items-center justify-center"
            style={{ borderRadius: '50%' }}
          >
            <MapPin size={24} />
          </Link>
          <Link to="/community" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Users size={22} />
            <span className="text-xs font-medium">Community</span>
          </Link>
          <Link to="/profile" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <User size={22} />
            <span className="text-xs font-medium">Profile</span>
          </Link>
        </div>
      </div>
      
      <FloatingSupport />
    </div>
  );
}
